import type { ModelDocument } from '../shared/contracts.js';
import { syncDocument } from '../core/model.js';

export function ddlExportRoots(document: ModelDocument) {
  const active = document.representations.find(r => r.representationId === document.model.activeRepresentation);
  const physical = active?.kind === 'physical' ? active : document.representations.find(r => r.kind === 'physical');
  return document.objects.filter(o => !o.ownerObjectId && o.representationId === physical?.representationId && o.lifecycle !== 'archived');
}

/** A transient export subset preserves identities and unresolved semantics so generation can still reject unsafe selections. */
export function selectDDLExport(document: ModelDocument, selectedRootIds: readonly string[]) {
  const roots = ddlExportRoots(document), selected = new Set(selectedRootIds);
  const included = new Set(roots.filter(o => selected.has(o.objectId)).map(o => o.objectId));
  for (let changed = true; changed;) {
    changed = false;
    for (const object of document.objects) if (object.ownerObjectId && included.has(object.ownerObjectId) && !included.has(object.objectId)) { included.add(object.objectId); changed = true; }
  }
  const omittedRoots = roots.filter(o => !selected.has(o.objectId));
  const omittedRelationships = document.relationships.filter(r => (included.has(r.sourceObjectId) || included.has(r.targetObjectId)) && (!included.has(r.sourceObjectId) || !included.has(r.targetObjectId)));
  const subset = structuredClone(document);
  const physical = roots[0]?.representationId ?? document.model.activeRepresentation;
  subset.objects = subset.objects.filter(o => included.has(o.objectId));
  subset.relationships = subset.relationships.filter(r => included.has(r.sourceObjectId) && included.has(r.targetObjectId));
  subset.representations = subset.representations.filter(r => r.representationId === physical);
  subset.diagrams = subset.diagrams.filter(d => d.representationId === physical).map(d => ({ ...d, appearances: d.appearances.filter(a => included.has(a.objectId)), connectors: d.connectors.filter(c => subset.relationships.some(r => r.relationshipId === c.relationshipId)) }));
  subset.model.activeRepresentation = physical;
  subset.model.modelType = 'physical';
  subset.model.originalConceptualBaseline = null;
  subset.mappings = [];
  syncDocument(subset);
  return { document: subset, omittedRoots, omittedRelationships };
}
